import ApplicationController from './application_controller';

/**
 * Shows and hides implementation survey questions as the admin answers the page they are on.
 *
 * The server decides which questions a page starts with (ImplementationSurvey::Walk). Each question
 * carries its show_if condition, with feature flags already resolved to { const: true|false }, and the
 * form carries the answers from earlier pages. On every change this re-evaluates each condition the way
 * ImplementationSurvey::Condition does and disables the inputs of hidden questions so they are not sent.
 *
 * @controller implementation-survey
 * @example please look at organizations/implementation_surveys/edit.html.erb.
 */
export default class extends ApplicationController {
  static targets = ['question'];

  static values = { answers: Object };

  connect() {
    this.update();
  }

  update() {
    const answers = { ...this.answersValue };
    this.questionTargets.forEach((question) => {
      const condition = question.dataset.condition ? JSON.parse(question.dataset.condition) : null;
      const visible = this.met(condition, answers);
      question.hidden = !visible;
      question.querySelectorAll('input, select, textarea').forEach((input) => {
        input.disabled = !visible;
      });
      if (visible) {
        const value = this.valueOf(question);
        if (value !== null) answers[question.dataset.key] = value;
      }
    });
  }

  valueOf(question) {
    const inputs = Array.from(question.querySelectorAll('input, select, textarea'));
    const values = inputs
      .filter((input) => !['checkbox', 'radio'].includes(input.type) || input.checked)
      .flatMap((input) =>
        input.multiple ? Array.from(input.selectedOptions).map((option) => option.value) : [input.value]
      )
      .filter((value) => value !== '');
    if (values.length === 0) return null;
    return question.dataset.multiple === 'true' ? values : values[0];
  }

  met(condition, answers) {
    if (!condition) return true;
    if ('const' in condition) return condition.const;
    if (condition.all) return condition.all.every((part) => this.met(part, answers));
    if (condition.any) return condition.any.some((part) => this.met(part, answers));
    if (condition.not) return !this.met(condition.not, answers);
    if (condition.answer) return this.answerMet(condition, answers[condition.answer]);
    return true;
  }

  answerMet(condition, answer) {
    if (answer === undefined || answer === null || answer === '' || (Array.isArray(answer) && answer.length === 0))
      return false;
    const values = (Array.isArray(answer) ? answer : [answer]).map(String);
    if (condition.in) return condition.in.map(String).some((value) => values.includes(value));
    if ('gt' in condition) return /^-?\d+(\.\d+)?$/.test(values[0]) && parseFloat(values[0]) > condition.gt;
    if ('min_selected' in condition)
      return values.filter((value) => value !== 'not_sure').length >= condition.min_selected;
    if (condition.present) return true;
    return false;
  }
}
